import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';

export interface AnomalyExample {
  index: number;
  value: any;
  anomaly_score?: number;
  type?: string;
}

export interface AnomalyRowData {
  index: number;
  anomaly_score?: number;
  [key: string]: any; // Full row data with all columns
}

export interface AnomalyListDialogData {
  anomalyIndices?: number[];
  anomalyExamples?: AnomalyExample[];
  anomalyRows?: AnomalyRowData[]; // Full row data for table-level anomalies
  methodFamily: string;
  totalRecords: number;
  columnName?: string;
  isColumnLevel: boolean;
}

@Component({
  selector: 'app-anomaly-list-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule
  ],
  templateUrl: './anomaly-list-dialog.component.html',
  styleUrls: ['./anomaly-list-dialog.component.scss']
})
export class AnomalyListDialogComponent {
  displayedColumns: string[] = [];
  dataColumns: string[] = []; // Actual data columns (excluding metadata columns like number, index)

  constructor(
    public dialogRef: MatDialogRef<AnomalyListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AnomalyListDialogData
  ) {
    console.log('Anomaly List Dialog initialized with data:', {
      isColumnLevel: data.isColumnLevel,
      hasAnomalyRows: !!data.anomalyRows,
      hasAnomalyIndices: !!data.anomalyIndices,
      hasAnomalyExamples: !!data.anomalyExamples,
      anomalyRowsCount: data.anomalyRows?.length,
      anomalyIndicesCount: data.anomalyIndices?.length,
      anomalyExamplesCount: data.anomalyExamples?.length,
      firstRow: data.anomalyRows?.[0],
      firstExample: data.anomalyExamples?.[0]
    });

    // Set columns based on data type
    if (data.isColumnLevel && data.anomalyExamples && data.anomalyExamples.length > 0) {
      // Column-level anomalies
      const firstExample = data.anomalyExamples[0];
      const allKeys = Object.keys(firstExample);
      
      // Check if we have enriched data (more than just the basic anomaly fields)
      const basicFields = ['index', 'value', 'anomaly_score', 'type'];
      const hasEnrichedData = allKeys.some(key => !basicFields.includes(key));
      
      if (hasEnrichedData) {
        // Enriched column-level anomalies with full row data
        this.dataColumns = allKeys.filter(key => 
          key !== 'index' && key !== 'value' && key !== 'anomaly_score' && key !== 'type'
        );
        this.displayedColumns = ['number', 'index', ...this.dataColumns, 'value', 'anomalyScore', 'type'];
        console.log('Column-level with enriched data - display columns:', this.displayedColumns);
      } else {
        // Legacy column-level anomalies (just the basic fields)
        this.displayedColumns = ['number', 'index', 'value', 'anomalyScore', 'type'];
        this.dataColumns = [];
        console.log('Column-level with basic fields only');
      }
    } else if (data.anomalyRows && data.anomalyRows.length > 0) {
      // Table-level with full row data - get all column names from the first row
      const firstRow = data.anomalyRows[0];
      this.dataColumns = Object.keys(firstRow).filter(key => 
        key !== 'index' && key !== 'anomaly_score'
      );
      this.displayedColumns = ['number', 'index', ...this.dataColumns, 'anomalyScore'];
      console.log('Table-level - display columns:', this.displayedColumns);
      console.log('Table-level - data columns:', this.dataColumns);
    } else {
      // Table-level with only indices (legacy)
      this.displayedColumns = ['number', 'rowIndex'];
      this.dataColumns = [];
      console.log('Fallback to legacy display (indices only)');
    }
  }

  get dataSource(): any[] {
    if (this.data.isColumnLevel) {
      return this.data.anomalyExamples || [];
    }
    if (this.data.anomalyRows) {
      return this.data.anomalyRows;
    }
    return this.data.anomalyIndices || [];
  }

  get anomalyCount(): number {
    if (this.data.isColumnLevel) {
      return this.data.anomalyExamples?.length || 0;
    }
    if (this.data.anomalyRows) {
      return this.data.anomalyRows.length;
    }
    return this.data.anomalyIndices?.length || 0;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  downloadAnomalies(): void {
    let csvContent: string;
    let filename: string;

    if (this.data.isColumnLevel && this.data.anomalyExamples) {
      // Column-level anomalies
      const firstExample = this.data.anomalyExamples[0];
      const allKeys = Object.keys(firstExample);
      
      // Check if we have enriched data
      const basicFields = ['index', 'value', 'anomaly_score', 'type'];
      const hasEnrichedData = allKeys.some(key => !basicFields.includes(key));
      
      if (hasEnrichedData && this.dataColumns.length > 0) {
        // Enriched column-level anomalies with full row data
        const headers = ['Index', ...this.dataColumns, 'Value', 'Anomaly Score', 'Type'];
        const csvRows = [headers.join(',')];
        
        this.data.anomalyExamples.forEach((example: any) => {
          const rowValues = [
            example.index,
            ...this.dataColumns.map(col => {
              const value = example[col];
              if (value !== null && value !== undefined) {
                const stringValue = String(value);
                if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                  return `"${stringValue.replace(/"/g, '""')}"`;
                }
                return stringValue;
              }
              return '';
            }),
            example.value,
            example.anomaly_score?.toFixed(6) || '',
            example.type || ''
          ];
          csvRows.push(rowValues.join(','));
        });
        
        csvContent = csvRows.join('\n');
        filename = `anomalies_${this.data.columnName}_enriched_${new Date().toISOString().split('T')[0]}.csv`;
      } else {
        // Basic column-level anomalies
        const headers = ['Index', 'Value', 'Anomaly Score', 'Type'];
        const csvRows = [headers.join(',')];
        
        this.data.anomalyExamples.forEach((example: any) => {
          const row = [
            example.index,
            example.value,
            example.anomaly_score?.toFixed(6) || '',
            example.type || ''
          ];
          csvRows.push(row.join(','));
        });

        csvContent = csvRows.join('\n');
        filename = `anomalies_${this.data.columnName}_${new Date().toISOString().split('T')[0]}.csv`;
      }
    } else if (this.data.anomalyRows) {
      // Table-level anomalies with full row data
      const headers = ['Index', ...this.dataColumns, 'Anomaly Score'];
      const csvRows = [headers.join(',')];
      
      this.data.anomalyRows.forEach((row: AnomalyRowData) => {
        const rowValues = [
          row.index,
          ...this.dataColumns.map(col => {
            const value = row[col];
            // Escape values that contain commas or quotes
            if (value !== null && value !== undefined) {
              const stringValue = String(value);
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }
            return '';
          }),
          row.anomaly_score?.toFixed(6) || ''
        ];
        csvRows.push(rowValues.join(','));
      });

      csvContent = csvRows.join('\n');
      filename = `table_level_anomalies_${this.data.methodFamily}_${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      // Table-level anomalies (legacy - just indices)
      const headers = ['Row Index'];
      const csvRows = [headers.join(',')];
      
      this.data.anomalyIndices?.forEach((index: number) => {
        csvRows.push(index.toString());
      });

      csvContent = csvRows.join('\n');
      filename = `table_level_anomalies_${this.data.methodFamily}_${new Date().toISOString().split('T')[0]}.csv`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
